import { useState, useEffect, useCallback } from 'react'
import { Toaster, toast } from 'react-hot-toast'
import { isAuthenticated, clearToken } from './lib/auth'
import { authFetch } from './lib/api'
import Login        from './components/Login'
import TaskSection  from './components/TaskSection'
import CategoryView from './components/CategoryView'
import LedgerView   from './components/LedgerView'
import QuickAdd     from './components/QuickAdd'
import BalancePanel from './components/BalancePanel'

const NS_KEY = 'ht_namespace'

export default function App() {
  const [authed,      setAuthed]      = useState(false)
  const [namespaces,  setNamespaces]  = useState([])
  const [namespace,   setNamespace]   = useState(null)   // current namespace object
  const [tasks,       setTasks]       = useState([])
  const [taskLoading, setTaskLoading] = useState(true)
  const [view,      setView]      = useState('status')
  const [payoutKey, setPayoutKey] = useState(0)      // bump to refresh BalancePanel

  useEffect(() => { setAuthed(isAuthenticated()) }, [])

  // ── Load namespaces once after auth ───────────────────────
  useEffect(() => {
    if (!authed) return
    authFetch('/api/namespaces').then(async res => {
      if (!res.ok) return
      const list = await res.json()
      setNamespaces(list)
      const saved = localStorage.getItem(NS_KEY)
      const initial = list.find(n => n.slug === saved) ?? list[0]
      setNamespace(initial)
    })
  }, [authed])

  // ── Load tasks whenever namespace changes ─────────────────
  const loadTasks = useCallback(async () => {
    if (!namespace) return
    const res = await authFetch(`/api/tasks?namespace=${namespace.slug}`)
    if (!res.ok) { toast.error('Could not load tasks'); setTaskLoading(false); return }
    setTasks(await res.json())
    setTaskLoading(false)
  }, [namespace])

  useEffect(() => {
    if (!namespace) return
    setTaskLoading(true)
    loadTasks()
    const onVisible = () => { if (document.visibilityState === 'visible') loadTasks() }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [namespace, loadTasks])

  // ── CRUD ──────────────────────────────────────────────────
  const handleToggleDone = useCallback(async (task) => {
    const markingDone = !task.done

    if (markingDone && namespace?.pocket_money_enabled) {
      // Auto-apply "good" rating when marking done in the pocket money namespace
      setTasks(prev => prev.map(p => p.id === task.id ? { ...p, done: true } : p))
      const res = await authFetch(`/api/tasks/${task.id}/evaluate`, {
        method: 'POST',
        body: JSON.stringify({ rating: 'good' }),
      })
      if (!res.ok) { toast.error('Could not update task'); loadTasks(); return }
      const updated = await res.json()
      setTasks(prev => prev.map(p => p.id === task.id ? updated : p))
      setPayoutKey(k => k + 1)
    } else {
      setTasks(prev => prev.map(p => p.id === task.id ? { ...p, done: !p.done } : p))
      const res = await authFetch(`/api/tasks/${task.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ done: !task.done }),
      })
      if (!res.ok) { toast.error('Could not update task'); loadTasks() }
      if (!markingDone && namespace?.pocket_money_enabled) setPayoutKey(k => k + 1)
    }
  }, [loadTasks, namespace])

  const handleDelete = useCallback(async (id) => {
    setTasks(prev => prev.filter(t => t.id !== id))
    const res = await authFetch(`/api/tasks/${id}`, { method: 'DELETE' })
    if (!res.ok) { toast.error('Could not delete task'); loadTasks() }
    else toast.success('Task removed')
  }, [loadTasks])

  const handleAdd = useCallback(async (taskData) => {
    const res = await authFetch('/api/tasks', {
      method: 'POST',
      body: JSON.stringify({ ...taskData, namespace: namespace.slug }),
    })
    if (!res.ok) throw new Error((await res.json()).error ?? 'Failed to add task')
    const data = await res.json()
    setTasks(prev => prev.find(t => t.id === data.id) ? prev : [data, ...prev])
  }, [namespace])

  const handleEdit = useCallback(async (taskId, fields) => {
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...fields } : t))
    const res = await authFetch(`/api/tasks/${taskId}`, {
      method: 'PATCH',
      body: JSON.stringify(fields),
    })
    if (!res.ok) { toast.error('Could not save edit'); loadTasks() }
  }, [loadTasks])

  const handleEvaluate = useCallback(async (taskId, rating) => {
    const res = await authFetch(`/api/tasks/${taskId}/evaluate`, {
      method: 'POST',
      body: JSON.stringify({ rating }),
    })
    if (!res.ok) { toast.error('Could not save evaluation'); return }
    const updated = await res.json()
    setTasks(prev => prev.map(t => t.id === taskId ? updated : t))
    setPayoutKey(k => k + 1)  // refresh balance
    toast.success(
      rating === 'awesome' ? 'Amazing! ⭐⭐⭐'
        : rating === 'good' ? 'Great job! ⭐⭐'
        : 'Good effort! ⭐'
    )
  }, [])

  const handleNamespaceChange = (ns) => {
    localStorage.setItem(NS_KEY, ns.slug)
    setNamespace(ns)
    setTasks([])
    setTaskLoading(true)
  }

  const handleLogout = () => { clearToken(); setAuthed(false); setTasks([]); setNamespace(null) }

  // ── Pre-auth ──────────────────────────────────────────────
  if (!authed) {
    return (
      <>
        <Toaster position="top-center" />
        <Login onLogin={() => setAuthed(true)} />
      </>
    )
  }

  const kids  = namespace?.slug === 'nishtha'

  return (
    <div className={`min-h-screen ${kids ? 'bg-purple-50' : 'bg-gray-50'} text-gray-900`}>
      <Toaster position="top-center" />

      {/* ── Header ── */}
      <header className={`sticky top-0 z-30 border-b shadow-sm ${
        kids
          ? 'bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 border-purple-400'
          : 'bg-white border-gray-100'
      }`}>
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center gap-3">
          <span className="text-xl">{kids ? '⭐' : '🏠'}</span>
          <h1 className={`text-base font-bold flex-1 ${kids ? 'text-white' : 'text-gray-800'}`}>
            {kids ? "Nishtha's Chores" : 'Household Tasks'}
          </h1>

          {/* Namespace selector */}
          {namespaces.length > 1 && (
            <div className={`flex items-center rounded-lg p-0.5 text-xs font-medium ${
              kids ? 'bg-white/20' : 'bg-gray-100'
            }`}>
              {namespaces.map(ns => (
                <button
                  key={ns.slug}
                  onClick={() => handleNamespaceChange(ns)}
                  className={`px-2.5 py-1 rounded-md transition ${
                    namespace?.slug === ns.slug
                      ? kids
                        ? 'bg-white text-purple-700 shadow-sm'
                        : 'bg-white text-gray-800 shadow-sm'
                      : kids
                        ? 'text-white/80 hover:text-white'
                        : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {ns.slug === 'nishtha' ? '⭐ ' : ''}{ns.display_name}
                </button>
              ))}
            </div>
          )}

          {/* View toggle */}
          <div className={`flex items-center rounded-lg p-0.5 text-xs font-medium ${
            kids ? 'bg-white/20' : 'bg-gray-100'
          }`}>
            {[
              { id: 'status',   label: 'Status'  },
              { id: 'category', label: 'Category' },
              { id: 'ledger',   label: '📒'       },
            ].map(({ id, label }) => (
              <button
                key={id}
                onClick={() => setView(id)}
                className={`px-2.5 py-1 rounded-md transition ${
                  view === id
                    ? kids ? 'bg-white text-purple-700 shadow-sm' : 'bg-white text-gray-800 shadow-sm'
                    : kids ? 'text-white/80 hover:text-white'    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Logout */}
          <button
            onClick={handleLogout}
            className={`p-1.5 rounded-lg transition focus:outline-none focus:ring-2 ${
              kids
                ? 'text-white/70 hover:text-white hover:bg-white/20 focus:ring-white'
                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:ring-gray-300'
            }`}
            title="Log out" aria-label="Log out"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16">
              <path d="M6 2H3a1 1 0 00-1 1v10a1 1 0 001 1h3M11 11l3-3-3-3M14 8H6"
                stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </header>

      {/* ── Main ── */}
      <main className="max-w-2xl mx-auto px-4 py-6">
        {view !== 'ledger' && (
          <>
            {/* Pocket money balance (Nishtha only) */}
            {namespace?.pocket_money_enabled && (
              <BalancePanel
                key={payoutKey}
                namespace={namespace}
                onPayout={loadTasks}
              />
            )}

            <QuickAdd onAdd={handleAdd} namespace={namespace} />
          </>
        )}

        {view === 'ledger' ? (
          <LedgerView namespace={namespace} />
        ) : taskLoading ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400 gap-3">
            <svg className="animate-spin w-8 h-8" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
            </svg>
            <span className="text-sm">Loading tasks…</span>
          </div>
        ) : view === 'status' ? (
          <TaskSection
            tasks={tasks}
            onToggleDone={handleToggleDone}
            onDelete={handleDelete}
            onEvaluate={handleEvaluate}
            onEdit={handleEdit}
            namespace={namespace}
          />
        ) : (
          <CategoryView
            tasks={tasks}
            onToggleDone={handleToggleDone}
            onDelete={handleDelete}
            onEvaluate={handleEvaluate}
            onEdit={handleEdit}
            namespace={namespace}
          />
        )}
      </main>
    </div>
  )
}
